import dayjs from 'dayjs';
import { Fragment, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { downloadFileFromBlob, downloadZipFile } from '@breathelife/file-helpers';
import { Box } from '@breathelife/mui';
import { OutcomeCode, Permission } from '@breathelife/types';
import { InfoSupplement } from '@breathelife/ui-components';

import { ApplicationModeBadge } from '../../../../Components/Badge/ApplicationModeBadge';
import { GenericSuccessStatusBadge } from '../../../../Components/Badge/GenericSuccessStatusBadge';
import { OutcomeBadge } from '../../../../Components/Badge/OutcomeBadge';
import { StatusBadge } from '../../../../Components/Badge/StatusBadge';
import { ContainerWithCopyButton } from '../../../../Components/ContainerWithCopyButton/ContainerWithCopyButton';
import { RestrictedToUserPermissions } from '../../../../Components/Restricted/RestrictedToUserPermissions';
import { useDispatch, useModalState } from '../../../../Hooks';
import { queryClient } from '../../../../ReactQuery';
import { useResetApplicationSubmissionStatusMutation } from '../../../../ReactQuery/ApplicationSupport/Application/application.mutations';
import { useGetSupportPageApplicationQuery } from '../../../../ReactQuery/ApplicationSupport/Application/application.queries';
import { QueryId } from '../../../../ReactQuery/common/common.types';
import { useUndeleteApplicationMutation } from '../../../../ReactQuery/Support/support.mutations';
import { notificationSlice } from '../../../../ReduxStore/Notification/NotificationSlice';
import ApiService from '../../../../Services/ApiService';
import { ActionsWidget } from './ActionsWidget/ActionsWidget';
import { ConfirmResetApplicationStatusModal } from './ConfirmResetApplicationStatusModal/ConfirmResetApplicationStatusModal';
import { ActionsWidgetContainer, EmptyGridElement, OverviewGridContainer } from './Styles';

// TODO HOT-1902: Centralize date formats in lead platform
export const AST_DATE_FORMAT = 'LLL';

export function ApplicationOverviewContainer(): ReactElement | null {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const { data: application } = useGetSupportPageApplicationQuery(applicationId);
  const { deletedAt: leadDeletedAt } = application || {};
  const [
    isResetSubmissonStatusConfirmationModalOpen,
    onOpenResetSubmissonStatusConfirmationModal,
    onCloseResetSubmissonStatusConfirmationModal,
  ] = useModalState();
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [isDownloadingAnswers, setIsDownloadingAnswers] = useState(false);
  const showApplicationUndeleteButton = !!leadDeletedAt;

  const undeleteApplicationMutation = useUndeleteApplicationMutation({
    onSuccess: () => {
      // Refetch the application data
      void queryClient.invalidateQueries([QueryId.applicationSupportApplications, applicationId]);

      dispatch(
        notificationSlice.actions.setSuccess({
          message: t('notifications.undeleteApplicationSuccess', { applicationId }),
          autoHideDuration: 5000,
        }),
      );
    },
  });

  const resetApplicationSubmissionStatusMutation = useResetApplicationSubmissionStatusMutation({
    onSuccess: useCallback(() => {
      void queryClient.invalidateQueries(QueryId.applicationSupportApplications);
    }, []),
  });

  const onDownloadSubmissionFiles = useCallback(async () => {
    if (!applicationId) return;

    setIsDownloadingFiles(true);

    let buffer: Buffer | undefined = undefined;

    try {
      buffer = await ApiService.getApplicationSupportApplicationSubmittedFiles(applicationId);
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: t('notifications.getSupportApplicationSubmittedFilesFailure', { applicationId }),
        }),
      );
    }

    if (buffer) {
      downloadZipFile(buffer, `${applicationId}_submission_files.zip`);
    }

    setIsDownloadingFiles(false);
  }, [applicationId]);

  const onDownloadAnswers = useCallback(() => {
    if (!application || !applicationId) return;

    setIsDownloadingAnswers(true);

    const answersData = application.answers;

    // Create download link
    const stringContent = JSON.stringify(answersData, null, 2);
    const blob = new Blob([stringContent], { type: 'application/json' });
    downloadFileFromBlob(blob, `${applicationId}_answers.json`);

    setIsDownloadingAnswers(false);
  }, [application, applicationId]);

  const onUndeleteApplication = useCallback(() => {
    if (!applicationId) return;

    undeleteApplicationMutation.mutate({ applicationId });
  }, [applicationId]);

  const onResetApplicationStatus = useCallback(
    (applicationId?: string) => {
      if (!applicationId) return;

      resetApplicationSubmissionStatusMutation.mutate(applicationId, {
        onSuccess: () => {
          onCloseResetSubmissonStatusConfirmationModal();
        },
      });
    },
    [resetApplicationSubmissionStatusMutation, onCloseResetSubmissonStatusConfirmationModal],
  );

  const notifyDataCopied = (data: string): void => {
    dispatch(
      notificationSlice.actions.setSuccess({
        message: t('notifications.dataCopied', { data }),
        autoHideDuration: 3000,
      }),
    );
  };

  if (!application) {
    return null;
  }

  const {
    refNo: policyNumber,
    createdAt: createdDate,
    updatedAt: lastModifiedDate,
    deletedAt: deletedDate,
    insuredCount,
    leadStatus: applicationStatus,
    carrierSubmissionStatus,
    outcomes,
    assignedToEmail,
    lineOfBusiness,
    mode,
  } = application;

  return (
    <Box display='flex'>
      <Box padding='2rem'>
        <ContainerWithCopyButton
          inputLabel={t('applicationSupport.applicationOverview.applicationUUID')}
          inputValue={applicationId}
          onClickCallback={notifyDataCopied}
          options={{
            buttonLabel: t('cta.copy'),
          }}
          marginBottom='2rem'
          width='28rem'
        />
        <ContainerWithCopyButton
          inputLabel={t('applicationSupport.applicationOverview.policyNumber')}
          inputValue={policyNumber}
          onClickCallback={notifyDataCopied}
          options={{
            buttonLabel: t('cta.copy'),
          }}
          marginBottom='3rem'
          width='28rem'
        />
        <OverviewGridContainer>
          <div>{t('applicationSupport.applicationOverview.currentAssignment')}</div>
          <div>{assignedToEmail}</div>

          <div>{t('applicationSupport.applicationOverview.createdDate')}</div>
          <div>{dayjs(createdDate).format(AST_DATE_FORMAT)}</div>

          <div>{t('applicationSupport.applicationOverview.lastModifiedDate')}</div>
          <div>{dayjs(lastModifiedDate).format(AST_DATE_FORMAT)}</div>

          {deletedDate && (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: '4px' }}>{t('applicationSupport.applicationOverview.deletedDate')}</div>
                <InfoSupplement
                  title={t('applicationSupport.applicationOverview.deletedDate')}
                  text={t('applicationSupport.applicationOverview.deletedDateTooltip')}
                />
              </div>
              <div>{dayjs(deletedDate).format(AST_DATE_FORMAT)}</div>
            </>
          )}

          {
            <Fragment>
              <div>{t('applicationSupport.applicationOverview.mode')}</div>
              <ApplicationModeBadge mode={mode} icon={true}></ApplicationModeBadge>
            </Fragment>
          }

          {
            <Fragment>
              <div>{t('applicationSupport.applicationOverview.lineOfBusiness')}</div>
              <div>{lineOfBusiness}</div>
            </Fragment>
          }

          {applicationStatus && (
            <Fragment>
              <div>{t('applicationSupport.applicationOverview.applicationStatus')}</div>
              <StatusBadge status={applicationStatus} />
            </Fragment>
          )}

          <div>{t('applicationSupport.applicationOverview.submissionStatus')}</div>
          {carrierSubmissionStatus ? (
            <GenericSuccessStatusBadge status={carrierSubmissionStatus} />
          ) : (
            <EmptyGridElement />
          )}

          <div>{t('applicationSupport.applicationOverview.totalNumberOfInsured')}</div>
          <div>{insuredCount}</div>

          {outcomes &&
            !!Object.keys(outcomes).length &&
            Object.entries(outcomes).map(([outcomeId, value], index) => (
              <Fragment key={index}>
                <div>{t('applicationSupport.applicationOverview.outcomeForInsured', { index: outcomeId })}</div>
                <OutcomeBadge outcome={value.outcome as OutcomeCode} />
              </Fragment>
            ))}
        </OverviewGridContainer>
      </Box>

      {/* Hiding the whole actions widget to avoid displaying an empty widget when there is no reset permission   */}
      {/* This approach will be revisited once we add more actions */}

      <RestrictedToUserPermissions requiredPermissions={[Permission.ApplicationSupportApplicationResetSubmittedStatus]}>
        <ActionsWidgetContainer>
          <ActionsWidget
            applicationId={application.id}
            refNo={application.refNo}
            applicationStatus={applicationStatus}
            onOpenResetSubmissonStatusConfirmationModal={onOpenResetSubmissonStatusConfirmationModal}
            onDownloadSubmissionFiles={onDownloadSubmissionFiles}
            isDownloadingFiles={isDownloadingFiles}
            isDownloadingAnswers={isDownloadingAnswers}
            onDownloadAnswers={onDownloadAnswers}
            isApplicationUndeleting={undeleteApplicationMutation.isLoading}
            onUndeleteApplication={onUndeleteApplication}
            showUndeleteApplicationButton={showApplicationUndeleteButton}
          />
        </ActionsWidgetContainer>
      </RestrictedToUserPermissions>

      <ConfirmResetApplicationStatusModal
        application={application}
        isResettingApplicationStatus={resetApplicationSubmissionStatusMutation.isLoading}
        isOpen={isResetSubmissonStatusConfirmationModalOpen}
        closeModal={onCloseResetSubmissonStatusConfirmationModal}
        onResetApplicationStatus={onResetApplicationStatus}
      />
    </Box>
  );
}
